import {
  LIMIT_RESPONSE_HEADER,
  LimitRequestHeaders,
  OPTIMIZED_QUERY_HEADER,
  QUERY_TRUNCATION_WARNING_PREFIX,
} from '@chronosphereio/shared';
import { FetchFn, fetch, fetchJson, RequestHeaders } from '../../core';
import { useVizFrameworkConfig, VizFrameworkConfigArgs } from '../../plugin-system';
import {
  InstantQueryRequestParameters,
  InstantQueryResponse,
  LabelNamesRequestParameters,
  LabelNamesResponse,
  LabelValuesRequestParameters,
  LabelValuesResponse,
  RangeQueryRequestParameters,
  RangeQueryResponse,
} from './api-types';

interface PrometheusClientOptions {
  datasourceUrl: string;
  headers?: RequestHeaders;
  fetchFn?: FetchFn;
}

export interface PrometheusClient {
  options: PrometheusClientOptions;
  instantQuery(params: InstantQueryRequestParameters, headers?: RequestHeaders): Promise<InstantQueryResponse>;
  rangeQuery(params: RangeQueryRequestParameters, headers?: RequestHeaders): Promise<RangeQueryResponse>;
  labelNames(params: LabelNamesRequestParameters, headers?: RequestHeaders): Promise<LabelNamesResponse>;
  labelValues(params: LabelValuesRequestParameters, headers?: RequestHeaders): Promise<LabelValuesResponse>;
}

export interface QueryOptions {
  datasourceUrl: string;
  headers?: RequestHeaders;
  fetchFn?: FetchFn;
}

/**
 * Calls the `/api/v1/query` endpoint to get metrics data.
 */
export function instantQuery(params: InstantQueryRequestParameters, queryOptions: QueryOptions) {
  return fetchWithPost<InstantQueryRequestParameters, InstantQueryResponse>('/api/v1/query', params, queryOptions);
}

/**
 * Calls the `/api/v1/query_range` endpoint to get metrics data.
 */
export function rangeQuery(params: RangeQueryRequestParameters, queryOptions: QueryOptions) {
  const { truncateAt, truncateBy, ...otherParams } = params;
  const modifiedParams = {
    ...otherParams,
    ['truncate-at']: truncateAt,
    ['truncate-by']: truncateBy,
  };
  return fetchWithPost<RangeQueryRequestParameters, RangeQueryResponse>(
    '/api/v1/query_range',
    modifiedParams,
    queryOptions
  );
}

/**
 * Calls the `/api/v1/labels` endpoint to get a list of label names.
 */
export function labelNames(params: LabelNamesRequestParameters, queryOptions: QueryOptions) {
  return fetchWithPost<LabelNamesRequestParameters, LabelNamesResponse>('/api/v1/labels', params, queryOptions);
}

/**
 * Calls the `/api/v1/label/{labelName}/values` endpoint to get a list of values for a label.
 */
export function labelValues(params: LabelValuesRequestParameters, queryOptions: QueryOptions) {
  const { labelName, ...searchParams } = params;
  const apiURI = `/api/v1/label/${encodeURIComponent(labelName)}/values`;
  return fetchWithGet<typeof searchParams, LabelValuesResponse>(apiURI, searchParams, queryOptions);
}

function fetchWithGet<T extends RequestParams<T>, TResponse>(apiURI: string, params: T, queryOptions: QueryOptions) {
  const { datasourceUrl, fetchFn } = queryOptions;

  let url = `${datasourceUrl}${apiURI}`;
  const urlParams = createSearchParams(params).toString();
  if (urlParams !== '') {
    url += `?${urlParams}`;
  }
  return fetchJson<TResponse>(fetchFn, url, { method: 'GET', headers: queryOptions.headers });
}

function fetchWithPost<T extends RequestParams<T>, TResponse>(apiURI: string, params: T, queryOptions: QueryOptions) {
  const { datasourceUrl, headers, fetchFn } = queryOptions;

  const url = `${datasourceUrl}${apiURI}`;
  const init = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      ...headers,
    },
    body: createSearchParams(params),
  };
  return fetchResults<TResponse>(fetchFn, url, init);
}

// Request parameter values we know how to serialize
type ParamValue = string | string[] | number | undefined;

// Used to constrain the types that can be passed to createSearchParams to
// just the ones we know how to serialize
type RequestParams<T> = {
  [K in keyof T]: ParamValue;
};

/**
 * Creates URLSearchParams from a request params object.
 */
function createSearchParams<T extends RequestParams<T>>(params: T) {
  const searchParams = new URLSearchParams();
  for (const key in params) {
    const value: ParamValue = params[key];
    if (value === undefined) continue;

    if (typeof value === 'string') {
      searchParams.append(key, value);
      continue;
    }

    if (typeof value === 'number') {
      searchParams.append(key, value.toString());
      continue;
    }

    for (const val of value) {
      searchParams.append(key, val);
    }
  }
  return searchParams;
}

/**
 * Fetch JSON and parse warnings for query inspector
 */
export async function fetchResults<T>(fetchFn: FetchFn = fetch, ...args: Parameters<typeof globalThis.fetch>) {
  const response = await fetchFn(...args);
  const json: T = await response.json();
  return { ...json, rawResponse: response };
}

export class PrometheusDatasource implements PrometheusClient {
  options: PrometheusClientOptions;

  constructor(options: PrometheusClientOptions) {
    this.options = options;
  }

  _getHeaders(headers: RequestHeaders = {}) {
    return { ...this.options.headers, ...headers };
  }

  async instantQuery(params: InstantQueryRequestParameters, headers?: RequestHeaders) {
    return instantQuery(params, { ...this.options, headers: this._getHeaders(headers) });
  }

  async rangeQuery(params: RangeQueryRequestParameters, headers?: RequestHeaders) {
    return rangeQuery(params, { ...this.options, headers: this._getHeaders(headers) }).then(processRangeQueryResponse);
  }

  async labelNames(params: LabelNamesRequestParameters, headers?: RequestHeaders) {
    return labelNames(params, { ...this.options, headers: this._getHeaders(headers) });
  }

  async labelValues(params: LabelValuesRequestParameters, headers?: RequestHeaders) {
    return labelValues(params, { ...this.options, headers: this._getHeaders(headers) });
  }
}

// Parse M3-Returned-Data-Limited response header and check if queries were truncated
function processRangeQueryResponse(results: RangeQueryResponse): Promise<RangeQueryResponse> {
  if (results.status !== 'success') {
    return Promise.resolve(results);
  }
  const limitResponseHeader = results.rawResponse?.headers?.get(LIMIT_RESPONSE_HEADER) ?? undefined;
  if (limitResponseHeader != null) {
    try {
      const partialResultsData = JSON.parse(limitResponseHeader);
      if (partialResultsData.Limited === true) {
        const resultInfo = `${QUERY_TRUNCATION_WARNING_PREFIX} ${partialResultsData.Series} out of ${partialResultsData.TotalSeries} total series.`;
        results.warnings = [resultInfo]; // extra info about partial results, to be displayed in impacted panels
      }
    } catch (e) {
      console.error(e);
    }
  }

  const optimizedQueryHeader = results.rawResponse?.headers?.get(OPTIMIZED_QUERY_HEADER) ?? undefined;
  if (optimizedQueryHeader != null) {
    const optimizedQueryInfo = `Query was rewritten by the optimizer to be: ${optimizedQueryHeader}`;
    results.infos = results.infos || [];
    results.infos.push(optimizedQueryInfo);
  }

  return Promise.resolve(results);
}

export const DEFAULT_PROM_URL = '/data/m3';

export function getPrometheusClient(config?: VizFrameworkConfigArgs, queryLimitsEnabled?: boolean, fetchFn?: FetchFn) {
  const { datapointsLimit, seriesLimit, prometheusURL, datasourceRequestHeaders } = config ?? {};

  const headers: RequestHeaders = {};

  // If query truncation is enabled, set the limit headers
  if (queryLimitsEnabled) {
    if (datapointsLimit) {
      headers[LimitRequestHeaders.DATA_POINTS] = String(datapointsLimit);
    }

    if (seriesLimit) {
      headers[LimitRequestHeaders.SERIES] = String(seriesLimit);
    }
  }

  const allHeaders = { ...headers, ...datasourceRequestHeaders };

  return new PrometheusDatasource({
    datasourceUrl: prometheusURL ?? DEFAULT_PROM_URL,
    headers: allHeaders,
    fetchFn,
  });
}

export function usePrometheusClient() {
  const config = useVizFrameworkConfig();
  return getPrometheusClient(config);
}
